/**
 * Per-account monthly-metrics client (per-account fee/billing arc, Inc 1c).
 *
 * Backend router: app/routers/account_metrics.py, prefix /account-monthly-metrics.
 *   - GET  /template                  the GENERIC per-account template (one row per
 *                                     contributing account, AuM blank, NO month column)
 *   - POST /import   (multipart)      bulk 20th-AuM import into the picked `period`
 *                                     (authoritative — stamped on every row), preview|commit
 *
 * Mirrors monthlyMetrics.ts: the template download streams a blob (browser save),
 * the import sends `mode` + `on_conflict` form fields alongside the file, and the
 * backend is STATELESS between calls (re-send the same File on commit). Distinct
 * from the agreement importer: this file is ROW-PER-ACCOUNT (a machine `account_id`
 * column keys the round-trip) and the template is generated for one chosen month.
 */
import { downloadBlob } from './iaaExports';

export type AccountMetricsImportMode = 'preview' | 'commit';
export type AccountMetricsImportConflict = 'skip' | 'overwrite';

/** Result of a per-account 20th-AuM import (preview or commit). On preview the
 *  inserted/overwritten/skipped counters stay 0 (nothing was written). */
export interface AccountMetricsImportReport {
  mode: AccountMetricsImportMode;
  on_conflict: AccountMetricsImportConflict;
  total_rows: number;            // data rows carrying a 20th-AuM value
  matched_accounts: number;      // rows whose account_id resolves to a live Active account
  unmatched_rows: string[];      // account_id values with no live Active account (sample)
  new_count: number;             // (account, month) rows not yet present
  conflict_count: number;        // rows whose (account, month) already exists
  conflict_sample: string[];     // ≤200 "<account#> YYYY-MM" — a SAMPLE
  errors: string[];              // parse/value problems (row skipped)
  inserted: number;
  overwritten: number;
  skipped: number;
  // EOM-import-only heads-up (empty for the 20th-AuM import). Non-blocking.
  reconciliation_warnings?: string[];  // Σ per-account EOM vs stored agreement-level AuM (the seam)
  stale_run_warnings?: string[];       // touched (agreement, month) already has a frozen fee run
  month_mismatch_warnings?: string[];  // reused template whose file month != the picked upload month
  flow_date_errors?: string[];         // EOM-only HARD REJECT: flow dates blank/out-of-month (upload refused)
  mtd_errors?: string[];               // EOM-only HARD REJECT: one agreement-date given two different MTDs
  agreement_mtd_count?: number;        // stored agreement-MTD overrides (informational)
  // EOM-only: does the upload introduce or alter FLOWS, not just AuM? File-vs-stored diff (Pedro 2026-07-27).
  flows_new_count?: number;            // account-months that GAIN flows not stored today
  flows_changed_count?: number;        // stored flow-sets the file modifies/removes (applied on Overwrite)
  flows_new_sample?: string[];         // ≤200 "<account#> YYYY-MM" of the new-flow account-months
  flows_changed_sample?: string[];     // ≤200 "<account#> YYYY-MM" of the changed-flow account-months
  // EOM-only MATERIAL-FLOW guard (§5.4): a net agreement flow > 10% of opening AuM needs an mtd%.
  material_flow_notes?: string[];      // material flow WITH an mtd% — will compute
  material_flow_warnings?: string[];   // material flow WITHOUT an mtd% — the agreement-month return is withheld
  material_gap_blocked?: boolean;      // a COMMIT was blocked pending the operator's Proceed
  // P35 (Pedro 2026-10-14), BOTH uploads: month-over-month flags vs the store's history.
  flag_new_accounts?: string[];        // first value ever → this is the account's start month
  flag_missing_accounts?: string[];    // value last month, none in the file or on record now
  flag_zero_accounts?: string[];       // 0 this month (file) or last month
  flag_gap_accounts?: string[];        // last value older than last month (a hole)
}

function fileNameFromCD(cd: string, fallback: string): string {
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1] : fallback;
}

async function detailOf(res: Response, fallback: string): Promise<string> {
  let detail: unknown = null;
  try { detail = (await res.json()).detail; } catch { /* non-JSON body */ }
  return typeof detail === 'string' ? detail : fallback;
}

export const accountMetricsApi = {
  /** Bulk import the filled per-account 20th-AuM file. preview parses + reconciles
   *  and writes nothing; commit applies (on_conflict 'overwrite' replaces existing
   *  account-months, 'skip' leaves them). Backend caches NOTHING — re-send the same
   *  File on commit. */
  async importFile(
    file: File,
    mode: AccountMetricsImportMode,
    onConflict: AccountMetricsImportConflict,
    period: string,
  ): Promise<AccountMetricsImportReport> {
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    form.append('on_conflict', onConflict);
    form.append('period', period);   // 'YYYY-MM' — authoritative; stamped on every row
    const res = await fetch('/account-monthly-metrics/import', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      throw new Error(await detailOf(res, 'The 20th-AuM file could not be processed.'));
    }
    return res.json();
  },

  /** Download the GENERIC per-account template (.xlsx): one row per contributing account,
   *  identity columns filled, the 20th-AuM column blank, NO month column (the month is chosen
   *  at upload via the picker). */
  async downloadTemplate(): Promise<void> {
    const res = await fetch('/account-monthly-metrics/template', {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) {
      throw new Error(await detailOf(res, 'The template could not be generated.'));
    }
    const cd = res.headers.get('Content-Disposition') ?? '';
    downloadBlob(await res.blob(), fileNameFromCD(cd, 'account_20th_aum_template.xlsx'));
  },

  /** EOM-AuM + flows (returns basis, ≥ Jan-2026). Mirrors the 20th-AuM import; fills
   *  the account-month row the 20th-AuM created. `on_conflict` acts on rows that
   *  already carry EOM. Backend caches NOTHING — re-send the same File on commit. */
  async importEomFile(
    file: File,
    mode: AccountMetricsImportMode,
    onConflict: AccountMetricsImportConflict,
    period: string,
    proceedMaterialGaps = false,   // §5.4: on commit, import despite material-flow gaps (returns withheld)
  ): Promise<AccountMetricsImportReport> {
    const form = new FormData();
    form.append('file', file);
    form.append('mode', mode);
    form.append('on_conflict', onConflict);
    form.append('period', period);   // 'YYYY-MM' — authoritative; stamped on every row
    form.append('proceed_material_gaps', String(proceedMaterialGaps));
    const res = await fetch('/account-monthly-metrics/eom-import', {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      throw new Error(await detailOf(res, 'The EOM-AuM & flows file could not be processed.'));
    }
    return res.json();
  },

  /** Download the month's per-account EOM-AuM + flows template (.xlsx): one row per
   *  contributing account, identity filled, flow dates seeded to the 15th, EOM /
   *  inflow / outflow blank. */
  async downloadEomTemplate(): Promise<void> {
    const res = await fetch('/account-monthly-metrics/eom-template', {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) {
      throw new Error(await detailOf(res, 'The template could not be generated.'));
    }
    const cd = res.headers.get('Content-Disposition') ?? '';
    downloadBlob(await res.blob(), fileNameFromCD(cd, 'account_eom_flows_template.xlsx'));
  },

  /** Read-only per-account 'peek' (.xlsx) over [from, to] ('YYYY-MM'): stored 20th-AuM (sheet 1)
   *  + frozen fee (sheet 2), accounts in rows, months in columns. Diagnostic; Admin-only. */
  async downloadPeek(fromM: string, toM: string): Promise<void> {
    const res = await fetch(
      `/account-monthly-metrics/peek?from=${encodeURIComponent(fromM)}&to=${encodeURIComponent(toM)}`,
      { method: 'GET', credentials: 'include' },
    );
    if (!res.ok) {
      throw new Error(await detailOf(res, 'The peek export could not be generated.'));
    }
    const cd = res.headers.get('Content-Disposition') ?? '';
    downloadBlob(await res.blob(), fileNameFromCD(cd, 'stored_data_peek.xlsx'));
  },
};
